import { Box, Typography } from '@mui/material'
import { type CollectionIndex, P2PIndexStatus } from '../../../../models/peerToPeer/Collection'
import { CARD_PALETTES, DistrictCard } from './DistrictCard'
import { OverviewActions } from './OverviewActions'
import { styles } from './ReportingOverview.styles'

interface Props {
  index: CollectionIndex
  onAddDistrict: () => void
  onOpenDistrict: (districtId: number, buildingIds: number[], focusBuildingId?: number) => void
  /**
   * Start or review a building that has no active-program form yet — routes to the
   * per-building Program Status prompt (Yes → its form; No → mark no program).
   */
  onStartBuilding: (districtId: number, buildingId: number) => void
  onSubmit: () => void
}

export const ReportingOverview = ({ index, onAddDistrict, onOpenDistrict, onStartBuilding, onSubmit }: Props): JSX.Element => {
  const allBuildings = index.districts.flatMap(district => district.buildings)
  // Once the report is submitted it is read-only to the District User — keep Submit Report
  // disabled so returning to the overview can't re-submit it.
  const alreadySubmitted = index.indexStatus === P2PIndexStatus.Submitted
  const allComplete = !alreadySubmitted && allBuildings.length > 0 && allBuildings.every(building => building.isComplete)

  return (
    <Box data-testid='p2pReportingOverview'>
      <Typography variant='h3'>Reporting Overview</Typography>
      <Typography variant='body2' sx={styles.intro}>
        We&apos;re glad you&apos;re here. Your progress is automatically saved, so you can complete your report at your own pace or come back to it at a later time.
      </Typography>

      <Typography variant='h4'>Your District(s)</Typography>
      <Typography variant='body2' sx={styles.districtsHint}>Add the district or districts you&apos;ll be reporting for. You can add more before submitting.</Typography>

      <Box sx={styles.cardRow}>
        {index.districts.map((district, position) => (
          <DistrictCard
            key={district.districtOrganizationId}
            district={district}
            palette={CARD_PALETTES[position % CARD_PALETTES.length]}
            onOpenBuilding={building => {
              if (building.requiredTotal > 0) {
                const withProgram = district.buildings.filter(b => b.requiredTotal > 0).map(b => b.organizationBuildingId)
                onOpenDistrict(district.districtOrganizationId, withProgram, building.organizationBuildingId)
              } else {
                onStartBuilding(district.districtOrganizationId, building.organizationBuildingId)
              }
            }}
          />
        ))}
        <OverviewActions canSubmit={allComplete} onAddDistrict={onAddDistrict} onSubmit={onSubmit} />
      </Box>
    </Box>
  )
}
